import * as React from 'react';
import { t } from '../../services/HelperService';
import {
  describeCommissionFilter,
  parseCommissionRuleArtifact,
  parseCommissionValueArtifact,
  readCommissionOutputs,
} from './commissionRuleArtifacts';

const safeString = (v) => (v ?? '').toString();
const S = (key) => t(`application_strings.views.commissions.ai.preview.${key}`);

const cardStyle = {
  border: '1px solid #dee2e6',
  borderRadius: 8,
  background: '#fff',
  padding: '10px 12px',
  display: 'flex',
  flexDirection: 'column',
  gap: 8,
};

const badgeStyle = (background, color) => ({
  display: 'inline-block',
  padding: '1px 8px',
  borderRadius: 10,
  fontSize: 11,
  fontWeight: 600,
  background,
  color,
  whiteSpace: 'nowrap',
});

const monoStyle = {
  fontFamily: 'Consolas, "Courier New", monospace',
  fontSize: 12,
  whiteSpace: 'pre-wrap',
  wordBreak: 'break-word',
  margin: 0,
};

const beforeStyle = { ...monoStyle, color: '#b02a37', textDecoration: 'line-through', background: '#fff5f5', borderRadius: 4, padding: '2px 6px' };
const afterStyle = { ...monoStyle, color: '#0f5132', background: '#f6fff8', borderRadius: 4, padding: '2px 6px' };
const sameStyle = { ...monoStyle, color: '#495057', background: '#f8f9fa', borderRadius: 4, padding: '2px 6px' };

/** One labelled field with before/after rendering; unchanged fields render once. */
const FieldDiff = ({ label, before, after, hasBefore, mono = true }) => {
  const b = safeString(before);
  const a = safeString(after);
  const changed = hasBefore && b !== a;
  const style = mono ? monoStyle : { fontSize: 12.5 };
  return (
    <div style={{ display: 'grid', gridTemplateColumns: '140px 1fr', gap: 8, alignItems: 'start' }}>
      <div style={{ fontSize: 11.5, fontWeight: 700, color: '#6c757d', paddingTop: 3 }}>{label}</div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4, minWidth: 0 }}>
        {changed && <pre style={{ ...style, ...beforeStyle }}>{b || '\u2014'}</pre>}
        <pre style={{ ...style, ...(changed ? afterStyle : (hasBefore ? sameStyle : afterStyle)) }}>{a || '\u2014'}</pre>
      </div>
    </div>
  );
};

const FilterLines = ({ lines, tone }) => {
  if (lines.length === 0) return <div style={{ fontSize: 12, color: '#6c757d', fontStyle: 'italic' }}>{S('noFilter')}</div>;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 2, fontSize: 12, ...(tone ?? {}) }}>
      {lines.map((line, ix) => (
        <div key={ix} style={{ paddingLeft: line.depth * 14 }}>
          {line.kind === 'logic'
            ? <span style={{ fontWeight: 700, color: '#6f42c1' }}>{line.text}</span>
            : (
              <span>
                <span style={{ fontWeight: 600 }}>{line.field}</span>
                {' '}
                <span style={{ color: '#6c757d' }}>{line.op}</span>
                {line.value ? <>{' '}<span style={{ fontFamily: 'Consolas, "Courier New", monospace' }}>{line.value}</span></> : null}
              </span>
            )}
        </div>
      ))}
    </div>
  );
};

const filterKey = (filter) => {
  try { return JSON.stringify(filter ?? null); } catch { return safeString(filter); }
};

const yesNo = (v) => (v ? t('Yes') : t('No'));

const ValueCard = ({ artifact, before, resolve }) => {
  const isNew = !before;
  const afterLines = describeCommissionFilter(artifact.Filter, resolve);
  const beforeLines = before ? describeCommissionFilter(before.Filter, resolve) : [];
  const filterChanged = !!before && filterKey(before.Filter) !== filterKey(artifact.Filter);
  return (
    <div style={{ ...cardStyle, borderLeft: `4px solid ${isNew ? '#28a745' : '#fd7e14'}` }} data-tagname={`commissionPreviewValue_${artifact.Name}`}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <div style={{ fontWeight: 700, fontSize: 13, flex: 1 }}>{artifact.Name}</div>
        <span style={isNew ? badgeStyle('#d1e7dd', '#0f5132') : badgeStyle('#ffe5d0', '#8a4b08')}>
          {isNew ? t('New') : t('Changed')}
        </span>
      </div>
      <FieldDiff label={S('expression')} before={before?.Expression} after={artifact.Expression} hasBefore={!isNew} />
      <div style={{ display: 'grid', gridTemplateColumns: '140px 1fr', gap: 8, alignItems: 'start' }}>
        <div style={{ fontSize: 11.5, fontWeight: 700, color: '#6c757d', paddingTop: 3 }}>{S('filter')}</div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {filterChanged && (
            <div style={{ borderLeft: '3px solid #dc3545', paddingLeft: 8, opacity: 0.75 }}>
              <div style={{ fontSize: 10.5, fontWeight: 700, textTransform: 'uppercase', color: '#b02a37' }}>{S('before')}</div>
              <FilterLines lines={beforeLines} />
            </div>
          )}
          <div style={{ borderLeft: `3px solid ${filterChanged || isNew ? '#28a745' : '#adb5bd'}`, paddingLeft: 8 }}>
            {filterChanged && <div style={{ fontSize: 10.5, fontWeight: 700, textTransform: 'uppercase', color: '#0f5132' }}>{S('after')}</div>}
            <FilterLines lines={afterLines} />
          </div>
        </div>
      </div>
      <FieldDiff label={S('groupBy')} before={before?.GroupBy} after={artifact.GroupBy} hasBefore={!isNew} />
      <FieldDiff label={S('summaryBy')} before={before?.SummaryBy} after={artifact.SummaryBy} hasBefore={!isNew} />
      <FieldDiff label={S('summaryType')} before={before ? t(before.SummaryType) : ''} after={t(artifact.SummaryType)} hasBefore={!isNew} mono={false} />
      <FieldDiff label={S('showOnStatement')} before={before ? yesNo(before.ShowOnStatement) : ''} after={yesNo(artifact.ShowOnStatement)} hasBefore={!isNew} mono={false} />
      <FieldDiff label={S('isText')} before={before ? yesNo(before.IsText) : ''} after={yesNo(artifact.IsText)} hasBefore={!isNew} mono={false} />
    </div>
  );
};

const RuleCard = ({ artifact, before, dateLabel }) => {
  const hasBefore = !!before && !!safeString(before.Expression).trim();
  return (
    <div style={{ ...cardStyle, borderLeft: '4px solid #6f42c1' }} data-tagname="commissionPreviewRule">
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <span style={badgeStyle('#e7e0f5', '#4b2a8a')}>{S('rule')}</span>
        <div style={{ fontWeight: 700, fontSize: 13, flex: 1 }}>{artifact.Name || before?.Name}</div>
      </div>
      {!!before && before.Name !== artifact.Name && !!artifact.Name && (
        <FieldDiff label={t('Name')} before={before.Name} after={artifact.Name} hasBefore mono={false} />
      )}
      <FieldDiff label={S('expression')} before={before?.Expression} after={artifact.Expression} hasBefore={hasBefore} />
      <FieldDiff label={S('commissionDateNo')} before={before ? dateLabel(before.CommissionDateNo) : ''} after={dateLabel(artifact.CommissionDateNo)} hasBefore={!!before} mono={false} />
      <FieldDiff label={t('Evaluation')} before={before ? (before.ByJob ? S('byJob') : S('perRep')) : ''} after={artifact.ByJob ? S('byJob') : S('perRep')} hasBefore={!!before} mono={false} />
      <FieldDiff label={S('textValues')} before={before?.TextValues} after={artifact.TextValues} hasBefore={!!before} />
    </div>
  );
};

/**
 * Profile-owned preview of the commissionRule session: the focused rule and every
 * value the assistant added or changed, each rendered as a BEFORE/AFTER diff against
 * what the editor holds (the host context the dialog sent — rules and values by
 * name). Filters are shown in words (field, operator, resolved names), never as raw
 * JSON, so an operator can judge the assistant's work without reading the artifact.
 *
 * Receives the dialog's generic preview props plus `hostContext` (the parsed
 * hostContextJson) and `templateCatalog` (whose lookup/product groups resolve tenant
 * ids to names).
 */
export const CommissionRulePreview = ({ outputs, hostContext, templateCatalog }) => {
  const { rule, values, invalid } = React.useMemo(() => readCommissionOutputs(outputs), [outputs]);

  const hostRules = React.useMemo(
    () => (Array.isArray(hostContext?.rules) ? hostContext.rules : []).map(parseCommissionRuleArtifact).filter((r) => r),
    [hostContext],
  );
  const hostValues = React.useMemo(
    () => (Array.isArray(hostContext?.values) ? hostContext.values : []).map(parseCommissionValueArtifact).filter((v) => v),
    [hostContext],
  );
  const focusedRuleName = safeString(hostContext?.focusedRule).trim();
  const focusedRule = hostRules.find((r) => r.Name.toLowerCase() === focusedRuleName.toLowerCase()) ?? null;

  // id -> display name from the client's own knowledge: the host legend and the
  // catalog groups (lookups and products) the prompt editor offers.
  const resolve = React.useMemo(() => {
    const map = new Map();
    const legend = hostContext?.legend;
    if (legend && typeof legend === 'object') {
      for (const [id, name] of Object.entries(legend)) {
        if (safeString(id).trim() && safeString(name).trim()) map.set(safeString(id).trim().toLowerCase(), safeString(name));
      }
    }
    for (const group of (Array.isArray(templateCatalog) ? templateCatalog : [])) {
      if (!group?.lookupKey && group?.group !== 'Products') continue;
      for (const item of (Array.isArray(group.items) ? group.items : [])) {
        const id = safeString(item?.token).trim().replace(/^["']|["']$/g, '').toLowerCase();
        if (id && item?.label && !map.has(id)) map.set(id, safeString(item.label));
      }
    }
    return (id) => map.get(safeString(id).trim().toLowerCase()) ?? null;
  }, [hostContext, templateCatalog]);

  const dateLabel = React.useCallback((no) => {
    const options = Array.isArray(hostContext?.commissionDateOptions) ? hostContext.commissionDateOptions : [];
    const found = options.find((o) => Number(o?.no) === Number(no));
    return found?.label || `${S('commissionDateNo')} ${no}`;
  }, [hostContext]);

  const ruleChanged = !!rule && (!focusedRule || JSON.stringify(rule) !== JSON.stringify(focusedRule));
  const newValues = values.filter((v) => !hostValues.some((h) => h.Name.toLowerCase() === v.Name.toLowerCase()));
  const changedValues = values.filter((v) => hostValues.some((h) => h.Name.toLowerCase() === v.Name.toLowerCase()));

  if (!ruleChanged && values.length === 0) {
    return (
      <div style={{ padding: 24, color: '#6c757d', fontSize: 13, lineHeight: 1.6 }}>
        <div style={{ fontWeight: 700, marginBottom: 6 }}>{S('noChanges')}</div>
        {focusedRule && (
          <div style={{ marginTop: 12 }}>
            <RuleCard artifact={focusedRule} before={null} dateLabel={dateLabel} />
          </div>
        )}
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12, padding: '8px 6px', background: '#f1f3f5', borderRadius: 6, overflowY: 'auto', flex: 1, minHeight: 0 }}>
      {invalid.length > 0 && (
        <div style={{ ...cardStyle, borderLeft: '4px solid #dc3545', color: '#842029', fontSize: 12 }}>
          {invalid.map((name) => t('application_strings.views.commissions.ai.invalidOutput', { name })).join(' ')}
        </div>
      )}
      {ruleChanged && <RuleCard artifact={rule} before={focusedRule} dateLabel={dateLabel} />}
      {newValues.length > 0 && (
        <>
          <div style={{ fontSize: 12, fontWeight: 700, color: '#495057', padding: '2px 4px' }}>{`${S('valuesAdded')} (${newValues.length})`}</div>
          {newValues.map((v) => <ValueCard key={v.Name} artifact={v} before={null} resolve={resolve} />)}
        </>
      )}
      {changedValues.length > 0 && (
        <>
          <div style={{ fontSize: 12, fontWeight: 700, color: '#495057', padding: '2px 4px' }}>{`${S('valuesChanged')} (${changedValues.length})`}</div>
          {changedValues.map((v) => (
            <ValueCard
              key={v.Name}
              artifact={v}
              before={hostValues.find((h) => h.Name.toLowerCase() === v.Name.toLowerCase())}
              resolve={resolve}
            />
          ))}
        </>
      )}
    </div>
  );
};
